import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { starImages } from '@/assets/img/common';
import { ticketImages } from '@/assets/img/lottery';
import { type productOfDayRewardsConfig } from '@/common/config/product-of-day-config';
import { ticketRewardList } from '@/common/constants/lottery';
import { useStorePrice } from '@/common/hooks/apple-price';
import { getIcon } from '@/common/lib/getIcon';
import {
  type ProductOfDayDataType,
  type ProductOfDayRewardItem,
} from '@/common/types/contract';
import { Picture } from '@/components/ui/Picture/Picture';

import { MainButton } from '../ui/Button/Button';
import { RewardByType } from '../ui/CommonReward/RewardByType';
import { RewardLabel } from '../ui/CommonReward/RewardLabel/RewardLabel';

import { Timer } from './Timer';

import css from './styles.module.scss';

interface ProductOfDayOfferProps {
  className?: string;
  product: ProductOfDayDataType;
  originalPrice?: number;
  discountPercent?: number;
  onBuy: () => void;
}

const ProductOfDayOffer: FC<ProductOfDayOfferProps> = ({
  className,
  product,
  discountPercent,
  onBuy,
}) => {
  const { t } = useTranslation();
  const productIcon = getIcon(product.name);

  const guaranteedTicket = product.rewardInfo.premium.guaranteedReward[0];
  const starPoints = product.rewardInfo.premium.extraStarPoints;
  const rewards = useMemo(
    () =>
      product.rewardInfo.premium.reward.reduce((acc, item) => {
        const existing = acc.find((r) => r.type === item.type);

        if (!existing) {
          acc.push({ ...item });
        } else if (item.value > existing.value) {
          existing.value = item.value;
        }

        return acc;
      }, [] as ProductOfDayRewardItem[]),
    [product],
  );

  product.rewardInfo.regular.reward.slice(0, 4);

  const price = product.upgradeCost.amountUsd;
  const discountedPrice = discountPercent
    ? Math.round(price * (1 - discountPercent / 100) * 100) / 100
    : price;
  // iOS app: what the App Store charges (the server bills the full upgrade
  // cost there; the discount is a Telegram display only)
  const storePrice = useStorePrice(price);

  return (
    <div
      className={cn(
        'relative rounded-2xl overflow-hidden',
        css.card,
        className,
      )}
    >
      <div className="flex flex-col items-center px-6 pb-6 relative z-[1]">
        {!!product.expiredAt && <Timer expiredAt={product.expiredAt} />}
        <span className="text-white/60 text-sm font-medium">
          {t('Daily goods')}
        </span>
        {productIcon && (
          <Picture
            className="size-[7.5rem]"
            img={productIcon.png}
            webp={productIcon.webp}
            alt={product.name}
          />
        )}
        <div className="flex justify-center gap-8 mb-[2.625rem] 720-h:mb-6">
          {guaranteedTicket && (
            <div className="flex items-center gap-1">
              <Picture
                className="size-10"
                img={ticketImages.gold3d.png}
                webp={ticketImages.gold3d.webp}
                alt="gold ticket"
              />
              <div className="flex flex-col gap-0.5">
                <span className="text-white text-[18px] font-bold leading-none">
                  +{guaranteedTicket.value}
                </span>
                <span className="text-white/60 text-[10px] leading-tight max-w-[3rem]">
                  {t('Gold Tickets')}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center gap-1">
            <div className="relative size-10">
              <Picture
                className="size-12 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                img={starImages.star3d.png}
                webp={starImages.star3d.webp}
                alt="star points"
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-white text-[18px] font-bold leading-none">
                +{starPoints}
              </span>
              <span className="text-white/60 text-[10px] leading-tight max-w-[3rem]">
                {t('Every contract')}
              </span>
            </div>
          </div>
        </div>
        <span className="text-white text-xl font-bold leading-none mb-1">
          {t('Upgrade rewards')}
        </span>
        <span className="text-white/60 text-sm font-medium mb-8 720-h:mb-4">
          {t('Get increased rewards for spins')}
        </span>
        <div className="flex justify-center gap-2 mb-9 720-h:mb-4">
          {rewards.map((reward) => {
            const type = reward.type as keyof typeof productOfDayRewardsConfig;
            const isUsd = reward.type === 'usdt';
            const isTicket = (ticketRewardList as string[]).includes(
              reward.type,
            );
            const additional = !isTicket
              ? {
                  text: 'up to',
                  className: 'text-[10px] text-white/60',
                }
              : undefined;
            const value = reward.value;

            return (
              <RewardByType
                type={type}
                size="64"
                label={() => (
                  <RewardLabel
                    className="gap-[2px] absolute bottom-[2px] left-1/2 -translate-x-1/2"
                    text={isUsd ? `$${value}` : String(value)}
                    additional={additional}
                  />
                )}
              />
            );
          })}
        </div>
        <MainButton
          onClick={onBuy}
          text={storePrice ?? `$ ${discountedPrice}`}
        />
      </div>
    </div>
  );
};

export { ProductOfDayOffer, type ProductOfDayOfferProps };
